import React, { useMemo } from "react";
import { SIZE, TYPE } from "components/Form";
import { useFormData } from "hooks";
import { css } from "@emotion/react";
import { fontSize } from "style";

/**
 * useTimeSheetFormData
 *
 * Custom hook for TimeSheet forms that reorganizes monthly review questions
 * to display squad name at top, followed by question header, options, textarea, and member questions
 * Handles multiple team/squad groups
 *
 * @param {Object} queryData
 */
export default function useTimeSheetFormData(queryData) {
  const { data: formData, initialValues, loading } = useFormData(queryData);

  const reorganizedData = useMemo(() => {
    if (!formData || formData.length === 0) return formData;

    const container = formData[0];
    if (!container?.items) return formData;

    const items = container.items;

    // Identify the structure:
    // - Xlarge label = question header (e.g., "Are you happy with your current mission?")
    // - Small label = squad name (e.g., "Platform Team 🚀")
    // - Radio (after small label) = squad question
    // - Textarea = open text field
    // - Xlarge labels = member question headers
    // - Small labels + radios = member questions

    const teamGroups = [];
    let currentGroup = null;
    let i = 0;

    while (i < items.length) {
      const item = items[i];

      // Check if this is the start of a new team group
      // A team group starts with an xlarge label (question header) followed by a small label (squad name)
      if (
        item.type === "label" &&
        item.size === SIZE.xlarge &&
        i + 1 < items.length &&
        items[i + 1].type === "label" &&
        items[i + 1].size === SIZE.small &&
        items[i + 1].label?.props?.to?.includes("/squads/")
      ) {
        // Save previous group if exists
        if (currentGroup) {
          teamGroups.push(currentGroup);
        }

        // Start new team group
        currentGroup = {
          questionHeader: item,
          squadName: items[i + 1],
          squadRadio: null,
          textarea: null,
          memberSections: [],
          currentMemberHeader: null,
          pendingMemberItems: [],
        };
        i += 2; // Skip both the header and squad name
        continue;
      }

      if (!currentGroup) {
        // If we haven't started a group yet, skip this item
        i++;
        continue;
      }

      // Collect squad radio (first radio after squad name)
      if (!currentGroup.squadRadio && item.properties?.direction === "horizontal" && item.properties?.keepLabelInline) {
        currentGroup.squadRadio = item;
        i++;
        continue;
      }

      // Check for member question header (xlarge label after squad radio)
      if (currentGroup.squadRadio && !currentGroup.textarea && item.type === "label" && item.size === SIZE.xlarge) {
        // Save previous member section if exists
        if (currentGroup.currentMemberHeader && currentGroup.pendingMemberItems.length > 0) {
          currentGroup.memberSections.push({
            header: currentGroup.currentMemberHeader,
            items: [...currentGroup.pendingMemberItems],
          });
          currentGroup.pendingMemberItems = [];
        }
        currentGroup.currentMemberHeader = item;
        i++;
        continue;
      }

      // Check for textarea (signals end of team group members)
      if (currentGroup.squadRadio && item.properties?.height === "medium" && item.properties?.placeholder) {
        // Save last member section if exists
        if (currentGroup.currentMemberHeader && currentGroup.pendingMemberItems.length > 0) {
          currentGroup.memberSections.push({
            header: currentGroup.currentMemberHeader,
            items: [...currentGroup.pendingMemberItems],
          });
          currentGroup.pendingMemberItems = [];
          currentGroup.currentMemberHeader = null;
        }
        currentGroup.textarea = item;
        i++;
        continue;
      }

      // Collect member items (labels and radios after member header)
      if (currentGroup.currentMemberHeader) {
        currentGroup.pendingMemberItems.push(item);
        i++;
        continue;
      }

      // Move to next item
      i++;
    }

    // Save last group
    if (currentGroup) {
      // Save any pending member section
      if (currentGroup.currentMemberHeader && currentGroup.pendingMemberItems.length > 0) {
        currentGroup.memberSections.push({
          header: currentGroup.currentMemberHeader,
          items: [...currentGroup.pendingMemberItems],
        });
      }
      teamGroups.push(currentGroup);
    }

    // Reconstruct items in desired order for all team groups
    const reorganizedItems = [];

    teamGroups.forEach((group, index) => {
      // 1. Squad name (xlarge)
      if (group.squadName) {
        reorganizedItems.push({
          ...group.squadName,
          type: TYPE.p,
          size: SIZE.xlarge,
          description: <span css={styles.squadName(!!index)}>{group.squadName.label}</span>,
        });
      }

      // 2. Question header (medium)
      if (group.questionHeader) {
        reorganizedItems.push({
          ...group.questionHeader,
          size: SIZE.medium,
        });
      }

      // 3. Squad radio (medium)
      if (group.squadRadio) {
        reorganizedItems.push({
          ...group.squadRadio,
          size: SIZE.medium,
        });
      }

      // 4. Textarea
      if (group.textarea) {
        reorganizedItems.push(group.textarea);
      }

      // 5. Member sections (header on separate line, then name + options on same line)
      group.memberSections.forEach((section) => {
        // Add member question header (keep original size, typically xlarge -> should be small per requirements)
        reorganizedItems.push(section.header);
        // Add all member items (name as small + radio options)
        section.items.forEach((item) => {
          reorganizedItems.push(item);
        });
      });

      reorganizedItems.push({ type: TYPE.h4, label: " " });
    });

    return [{ items: reorganizedItems }];
  }, [formData]);

  return {
    data: reorganizedData,
    initialValues,
    loading,
  };
}

const styles = {
  squadName: (isFirst = false) => css`
    font-size: ${fontSize.large};
    ${isFirst && `margin-top: 1.5rem;`}
  `,
};
